import type { MaterialId } from '@shared/rfq';
import { useI18n } from '@/i18n';
import { containerViewBox } from './geometry';
import { ProductBack, ProductGloss } from './ProductSvg';
import type { ContainerModel } from './model';
import type { Derived, ProductState } from './state';

/** Static thumbnail of a container in a material. */
export function ProductThumb({ c, material, id }: { c: ContainerModel; material: MaterialId; id: string }) {
  const vb = containerViewBox(c);
  return (
    <svg viewBox={`${vb.x} ${vb.y} ${vb.w} ${vb.h}`} className="jar-thumb" aria-hidden="true">
      <ProductBack c={c} material={material} uid={id} />
      <ProductGloss c={c} material={material} uid={id} />
    </svg>
  );
}

/** Human label of a closure, e.g. "кришка · дерево". */
export function useClosureLabel() {
  const { d } = useI18n();
  return (c: ContainerModel): string | null => {
    const cl = c.closure;
    if (!cl) return null;
    const kind = d.cfg.closures[cl.kind];
    return cl.kind === 'cork' || cl.kind === 'reeds' || cl.kind === 'swing' || cl.kind === 'tcork' || cl.finish === 'cork' ? kind : `${kind} · ${d.cfg.closures[cl.finish]}`;
  };
}

export function usePlacementWarnings() {
  const { d } = useI18n();
  const w = d.cfg.warnings;
  return (s: ProductState, der: Derived): { level: 'warn' | 'info'; text: string }[] => {
    const out: { level: 'warn' | 'info'; text: string }[] = [];
    if (der.dpiLevel === 'low' && der.dpi != null) out.push({ level: 'warn', text: w.lowDpi.replace('{dpi}', String(der.dpi)) });
    if (der.dpiLevel === 'ok' && der.dpi != null) out.push({ level: 'info', text: w.midDpi.replace('{dpi}', String(der.dpi)) });
    if (der.outsideBand) out.push({ level: 'warn', text: w.outsideBand });
    if (der.hitsNoPrint) out.push({ level: 'warn', text: w.noPrintZone });
    out.push(...inkWarnings(s.material, s.ink, w));
    return out;
  };
}

export function inkWarnings(material: MaterialId, ink: string, w: { blackNoWhite: string; clearNoWhite: string }) {
  const out: { level: 'warn' | 'info'; text: string }[] = [];
  if (ink !== 'cmyk') return out;
  if (material === 'black' || material === 'powder_black' || material === 'powder_navy') out.push({ level: 'warn', text: w.blackNoWhite });
  else if (material !== 'white' && material !== 'powder_white') out.push({ level: 'info', text: w.clearNoWhite });
  return out;
}
